import { Link } from "react-router-dom";
import {
    Award, BookOpen, Users, Globe2, Target, Heart, Compass, ShieldCheck,
    Sparkles, Trophy, GraduationCap, Video, Clock, MessageCircle, ArrowRight, Phone,
    BadgeCheck, MapPin, Quote, CheckCircle2, TrendingUp, Briefcase,
} from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import { SOCIAL, HEAD_OFFICE } from "../data/siteData";
import { QuickAnswerCard, ConversationalFAQ, buildConversationalFAQSchema } from "../components/AEOContent";
import { ABOUT_AEO } from "../data/aeoContent";

export default function About() {
    const aboutSchema = {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "url": "https://anandtutorials.com/about",
        "name": "About Anand Tutorials",
        "description":
            "Anand Tutorials is a global 1:1 home tuition and live online tutoring brand based in Jabalpur. 12+ years, 50,000+ students, 27 countries, 98% pass rate.",
        "mainEntity": {
            "@type": "EducationalOrganization",
            "name": "Anand Tutorials",
            "url": "https://anandtutorials.com",
            "foundingDate": "2013",
            "founder": { "@type": "Person", "name": "Anand Tutorials Founding Team" },
            "areaServed": ["India", "United States", "United Kingdom", "Canada", "Australia", "UAE", "Singapore", "Worldwide"],
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "Ghadi Chowk, Vijay Nagar",
                "addressLocality": "Jabalpur",
                "addressRegion": "Madhya Pradesh",
                "postalCode": "482002",
                "addressCountry": "IN",
            },
            "sameAs": [SOCIAL.facebook, SOCIAL.instagram].filter(Boolean),
        },
    };

    return (
        <div data-testid="about-page">
            <SEO
                title="About Anand Tutorials — A Global 1:1 Tutoring Brand from India"
                description="Since 2013, Anand Tutorials has helped 50,000+ students across India and 27 countries through verified 1-on-1 home tuition and live online classes. Discover our mission, values, tutor philosophy and global vision."
                keywords="about Anand Tutorials, best home tuition company India, premium online tutoring, expert home tutors, 1-on-1 tutoring brand, Jabalpur tutoring, Bhopal tutoring, Nagpur tutoring, global Indian tutors, NRI tutoring, IB IGCSE A-Level tutors India"
                path="/about"
                schemas={[aboutSchema, buildConversationalFAQSchema(ABOUT_AEO.faqs)]}
            />

            <PageHeader
                eyebrow="About Anand Tutorials"
                title="One tutor. One student. One future, taken seriously."
                subtitle="Since 2013, we've turned the noise of mass coaching into the silence of focused 1:1 mentoring — for over 50,000 students, across 27 countries, with one promise: the right tutor for your child, and full ownership of the outcome."
            >
                <div className="flex flex-wrap gap-3">
                    <Link to="/register" data-testid="about-cta-demo" className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-semibold px-5 py-3 rounded-lg hover:bg-amber-400 transition-colors">
                        Book a Free 1:1 Demo <ArrowRight className="w-4 h-4" />
                    </Link>
                    <a href="tel:+917000530316" data-testid="about-cta-call" className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur text-white font-semibold px-5 py-3 rounded-lg hover:bg-white/15">
                        <Phone className="w-4 h-4" /> Call Us
                    </a>
                </div>
            </PageHeader>

            {/* V65 — AEO Quick-Answer card */}
            <QuickAnswerCard
                question={ABOUT_AEO.quickAnswer.question}
                answer={ABOUT_AEO.quickAnswer.answer}
                bullets={ABOUT_AEO.quickAnswer.bullets}
                footer={ABOUT_AEO.quickAnswer.footer}
                testId="about-quick-answer"
            />

            {/* IMPACT STATS — premium dark strip */}
            <section className="bg-[#0F172A] text-white relative overflow-hidden" data-testid="about-impact-stats">
                <div className="absolute inset-0 bg-grid opacity-10"></div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                    {[
                        { n: "12+", l: "Years of trust", s: "Since 2013" },
                        { n: "50,000+", l: "Students mentored", s: "India · NRI · International" },
                        { n: "2,500+", l: "Verified tutors", s: "12% acceptance rate" },
                        { n: "98%", l: "Board pass rate", s: "CBSE · ICSE · MP Board" },
                    ].map((s) => (
                        <div key={s.l}>
                            <div className="font-heading text-4xl sm:text-5xl font-extrabold text-amber-400 mb-1">{s.n}</div>
                            <div className="text-sm font-semibold text-white">{s.l}</div>
                            <div className="text-xs uppercase tracking-[0.2em] text-blue-300 mt-1">{s.s}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* OUR STORY */}
            <section className="py-20 bg-white" data-testid="about-our-story">
                <div className="max-w-6xl mx-auto px-6 lg:px-8 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-start">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Our Story</div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight mb-6">
                            We started with one belief — and never stopped honouring it.
                        </h2>
                        <div className="space-y-5 text-slate-700 leading-relaxed text-base sm:text-lg">
                            <p>
                                In 2013, on a quiet street in Jabalpur, a small group of educators noticed something unsettling. Bright children — full of curiosity at home — were quietly losing confidence inside coaching halls of seventy or eighty students. Doubts went unasked. Mistakes went uncorrected. Parents paid more, year after year, and felt less in control of their child's progress.
                            </p>
                            <p>
                                We started Anand Tutorials with one promise: <span className="font-semibold text-[#0F172A]">no child of ours will ever be just a face in the crowd.</span> Every student deserves a tutor who knows their name, their gaps, their habits — and their dreams.
                            </p>
                            <p>
                                Twelve years later, that promise has travelled further than we imagined — from a single living room in Vijay Nagar to homes in Bhopal, Nagpur, Mumbai, Delhi, London, New Jersey, Toronto and Dubai. Same promise. Same standard. Same human teacher on the other end of the table — or the screen.
                            </p>
                        </div>
                    </div>

                    {/* Story sidecard */}
                    <div className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] text-white rounded-3xl p-8 sticky top-24 shadow-xl">
                        <Quote className="w-10 h-10 text-amber-400 mb-4" />
                        <p className="font-heading text-xl leading-relaxed text-blue-50 mb-6">
                            "We don't sell tuition. We take responsibility for a year of a child's life. That changes how we hire, how we teach, and how we measure ourselves."
                        </p>
                        <div className="border-t border-white/15 pt-4">
                            <div className="font-semibold">Anand Tutorials</div>
                            <div className="text-xs uppercase tracking-[0.2em] text-blue-200 mt-0.5">Founding philosophy · 2013</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MISSION · VISION · VALUES */}
            <section className="py-20 bg-[#F8FAFC] border-y border-slate-200" data-testid="about-mvv">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">What we stand for</div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A]">
                            Mission, vision, and the values we refuse to compromise.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-3 gap-6">
                        {[
                            {
                                I: Target,
                                eyebrow: "Our Mission",
                                t: "Make world-class 1:1 tutoring affordable for every family.",
                                d: "From Nursery to Engineering, from Jabalpur to Johannesburg — every child should be able to access a senior, well-matched tutor without paying metropolitan fees. Personalised education must stop being a luxury.",
                                color: "text-[#1E3A8A]",
                                bg: "bg-blue-50",
                            },
                            {
                                I: Compass,
                                eyebrow: "Our Vision",
                                t: "Be India's most trusted education export — globally.",
                                d: "We see Anand Tutorials as a global Indian education brand: home in 50+ Indian cities, online in 50+ countries, recognised the world over for the quality of an Indian teacher who actually cares about your child's mark sheet.",
                                color: "text-amber-600",
                                bg: "bg-amber-50",
                            },
                            {
                                I: Heart,
                                eyebrow: "Our Values",
                                t: "Trust earned through consistency — never marketing.",
                                d: "Verified tutors. Transparent pricing. Honest progress reports. Accountability for outcomes — even when it's inconvenient. We grow only as fast as our reputation can carry us.",
                                color: "text-rose-600",
                                bg: "bg-rose-50",
                            },
                        ].map((c) => (
                            <div key={c.eyebrow} className="bg-white rounded-3xl border border-slate-200 p-8 hover:border-blue-200 hover:shadow-md transition-all">
                                <div className={`w-14 h-14 rounded-2xl ${c.bg} ${c.color} flex items-center justify-center mb-5`}>
                                    <c.I className="w-7 h-7" />
                                </div>
                                <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2">{c.eyebrow}</div>
                                <h3 className="font-heading text-xl font-bold text-[#0F172A] mb-3 leading-snug">{c.t}</h3>
                                <p className="text-slate-600 leading-relaxed text-sm">{c.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* THE ANAND WAY — DIFFERENTIATORS */}
            <section className="py-20 bg-white" data-testid="about-the-way">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-3xl mb-12">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">The Anand Way</div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight mb-4">
                            Six things we do differently — and never apologise for.
                        </h2>
                        <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                            These aren't features in a brochure. They're the standards that have kept parents recommending us for 12 straight years.
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {[
                            { I: Users, t: "Strictly 1:1 — never a batch.", d: "One tutor, one student, one customised plan. Group classes don't appear in any of our 50,000 student journeys, and never will." },
                            { I: ShieldCheck, t: "Tutors are verified — not auditioned.", d: "Background check, qualification check, demo evaluation, parent feedback loop. Less than 12% of applicants make it into our network." },
                            { I: TrendingUp, t: "We measure outcomes, not hours.", d: "Tutors are reviewed against student progress, not class count. Sunday parent reports keep everyone honest, every week." },
                            { I: Video, t: "Same teacher — at home or online.", d: "Switch between home tuition and live 1:1 online classes any day. Same tutor, same plan, same continuity. Zero disruption." },
                            { I: Briefcase, t: "Coordinator-owned relationships.", d: "Every family is owned by a named coordinator. No call-centre routing, no chatbots, no five-rep ping-pong. One person, one number, real accountability." },
                            { I: BadgeCheck, t: "Money-back, not money-trapped.", d: "Free demo before any payment. Unused sessions refundable. Free tutor replacement if you're not satisfied. We win by results, not lock-in." },
                        ].map((c) => (
                            <article key={c.t} className="p-6 bg-[#F8FAFC] rounded-2xl border border-slate-200 hover:border-blue-200 transition-colors">
                                <div className="w-12 h-12 rounded-xl bg-white text-[#1E3A8A] flex items-center justify-center mb-4 shadow-sm border border-slate-100">
                                    <c.I className="w-6 h-6" />
                                </div>
                                <h3 className="font-heading font-semibold text-[#0F172A] mb-2 leading-snug">{c.t}</h3>
                                <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* TUTOR PHILOSOPHY — selection funnel */}
            <section className="py-20 bg-[#0F172A] text-white relative overflow-hidden" data-testid="about-tutors">
                <div className="absolute inset-0 bg-grid opacity-10"></div>
                <div className="relative max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-400 mb-3">Tutor Philosophy</div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6">
                            We hire the way IIT-Bombay hires faculty — not the way coaching ads hire.
                        </h2>
                        <div className="space-y-4 text-blue-100 leading-relaxed">
                            <p>
                                Most platforms approve tutors after a phone call. We don't. Every Anand Tutorials tutor goes through a four-stage gauntlet — and we reject the majority.
                            </p>
                            <p>
                                The tutors who survive are not just academically strong. They're patient. They're punctual. They explain hard things in calm voices. They read a student's silence as fast as their answers. That's the bar.
                            </p>
                        </div>
                        <div className="mt-8 grid grid-cols-2 gap-4 text-sm">
                            {[
                                ["IIT · IIM · AIIMS · NIT alumni", "in our top-tier mentor pool"],
                                ["Cambridge · Oxford · Ivy League", "for IB / IGCSE / AP / SAT"],
                                ["Senior school faculty", "for board specialisation"],
                                ["AIIMS / GMC mentors", "for NEET strategy programs"],
                            ].map(([t, d]) => (
                                <div key={t} className="border-l-2 border-amber-400 pl-3">
                                    <div className="font-semibold text-white">{t}</div>
                                    <div className="text-xs text-blue-200 mt-0.5">{d}</div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Selection funnel */}
                    <div className="bg-white/5 backdrop-blur border border-white/15 rounded-3xl p-8">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-400 mb-2">Selection funnel</div>
                        <div className="font-heading text-2xl font-bold mb-6">From 100 applicants → 12 tutors.</div>
                        {[
                            { n: "100%", t: "Applications received", d: "Online + walk-in across all branches" },
                            { n: "62%", t: "Pass document & qualification check", d: "Government ID, marksheets, address proof" },
                            { n: "34%", t: "Clear the demo evaluation", d: "Live mock class with senior faculty" },
                            { n: "18%", t: "Pass background verification", d: "Independent reference checks" },
                            { n: "12%", t: "Become Anand Tutors", d: "Onboarded with weekly mentoring", final: true },
                        ].map((s, i) => (
                            <div key={s.t} className={`flex items-start gap-4 py-3 ${i !== 4 ? "border-b border-white/10" : ""}`}>
                                <div className={`font-heading text-2xl font-bold w-16 shrink-0 ${s.final ? "text-amber-400" : "text-white"}`}>{s.n}</div>
                                <div>
                                    <div className={`font-semibold ${s.final ? "text-amber-400" : "text-white"}`}>{s.t}</div>
                                    <div className="text-xs text-blue-200 mt-0.5">{s.d}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* PILLARS — what 1:1 actually means */}
            <section className="py-20 bg-white" data-testid="about-pillars">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-3xl mb-12">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">What "1:1" actually means here</div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight">
                            Personalised learning isn't a slogan. It's a methodology.
                        </h2>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        {[
                            { I: Sparkles, t: "Diagnostic-led teaching", d: "Every engagement starts with a 30-minute concept-gap diagnosis. The teaching plan is then built around your child — not lifted off a textbook chapter list." },
                            { I: Clock, t: "Pacing that respects the child", d: "When concepts click, we move on. When they don't, we slow down — without judgement, without embarrassment, without parental guilt." },
                            { I: GraduationCap, t: "Board-specific exam intelligence", d: "CBSE marks differently from ICSE; IB grades differently from A-Level. Our tutors are matched to your child's exact board so question patterns and scoring strategy are spot-on." },
                            { I: Trophy, t: "Outcomes audited weekly", d: "Sunday parent reports describe what was covered, what was understood, what's next, and how the child is responding emotionally — not just academically." },
                        ].map((c) => (
                            <article key={c.t} className="p-7 bg-gradient-to-br from-blue-50/50 to-amber-50/30 rounded-2xl border border-slate-200">
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 rounded-xl bg-white text-[#1E3A8A] flex items-center justify-center shrink-0 shadow-sm">
                                        <c.I className="w-6 h-6" />
                                    </div>
                                    <div>
                                        <h3 className="font-heading font-bold text-lg text-[#0F172A] mb-1.5">{c.t}</h3>
                                        <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            </section>

            {/* GLOBAL PRESENCE */}
            <section className="py-20 bg-[#F8FAFC] border-y border-slate-200" data-testid="about-presence">
                <div className="max-w-7xl mx-auto px-6 lg:px-8">
                    <div className="max-w-3xl mb-10">
                        <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">Where we teach</div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight mb-4">
                            Three home cities. Eleven Indian metros. Twenty-seven countries.
                        </h2>
                        <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
                            Anand Tutorials is headquartered at <span className="font-semibold">Ghadi Chowk, Vijay Nagar, Jabalpur</span>, with feet-on-the-ground branches in Bhopal and Nagpur, Pan-India online coverage, and dedicated international programs for the diaspora and global parents.
                        </p>
                        <div className="mt-4">
                            <a
                                href={HEAD_OFFICE.mapUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="about-head-office-directions"
                                className="inline-flex items-center gap-2 text-[#1E3A8A] hover:text-[#1E40AF] text-sm font-semibold underline underline-offset-2"
                            >
                                <MapPin className="w-4 h-4" /> Get Head Office directions on Google Maps
                            </a>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-6">
                        {/* Home cities */}
                        <div className="bg-white rounded-3xl p-7 border border-slate-200">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center">
                                    <MapPin className="w-5 h-5" />
                                </div>
                                <div className="font-heading text-lg font-bold text-[#0F172A]">Home Cities</div>
                            </div>
                            <p className="text-xs text-slate-500 mb-4 uppercase tracking-[0.15em]">Home tuition · physical branches</p>
                            <ul className="space-y-2 text-sm">
                                {[
                                    ["Jabalpur", "/jabalpur", "HQ · Vijay Nagar"],
                                    ["Bhopal", "/bhopal", "MP Nagar branch"],
                                    ["Nagpur", "/nagpur", "Borgaon Road branch"],
                                ].map(([n, h, s]) => (
                                    <li key={n}>
                                        <Link to={h} className="flex items-center justify-between p-3 rounded-lg hover:bg-blue-50 group transition-colors">
                                            <div>
                                                <div className="font-semibold text-[#0F172A]">{n}</div>
                                                <div className="text-xs text-slate-500">{s}</div>
                                            </div>
                                            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-[#1E3A8A] group-hover:translate-x-0.5 transition-all" />
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* Indian metros */}
                        <div className="bg-white rounded-3xl p-7 border border-slate-200">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-blue-100 text-[#1E3A8A] flex items-center justify-center">
                                    <Briefcase className="w-5 h-5" />
                                </div>
                                <div className="font-heading text-lg font-bold text-[#0F172A]">Pan-India · Online</div>
                            </div>
                            <p className="text-xs text-slate-500 mb-4 uppercase tracking-[0.15em]">1:1 live online tuition</p>
                            <div className="grid grid-cols-2 gap-2 text-sm">
                                {[
                                    ["Mumbai", "/home-tuition-mumbai"],
                                    ["Delhi NCR", "/home-tuition-delhi"],
                                    ["Bangalore", "/home-tuition-bangalore"],
                                    ["Pune", "/home-tuition-pune"],
                                    ["Hyderabad", "/home-tuition-hyderabad"],
                                    ["Chennai", "/home-tuition-chennai"],
                                    ["Kolkata", "/home-tuition-kolkata"],
                                    ["Ahmedabad", "/home-tuition-ahmedabad"],
                                ].map(([n, h]) => (
                                    <Link key={n} to={h} className="px-3 py-2 rounded-lg bg-slate-50 hover:bg-blue-50 hover:text-[#1E3A8A] text-slate-700 font-medium text-xs">
                                        {n} →
                                    </Link>
                                ))}
                            </div>
                        </div>

                        {/* International */}
                        <div className="bg-gradient-to-br from-[#1E3A8A] to-[#1E40AF] rounded-3xl p-7 text-white">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-white/15 text-amber-400 flex items-center justify-center">
                                    <Globe2 className="w-5 h-5" />
                                </div>
                                <div className="font-heading text-lg font-bold">International</div>
                            </div>
                            <p className="text-xs text-blue-200 mb-4 uppercase tracking-[0.15em]">Dedicated country programs</p>
                            <ul className="space-y-1.5 text-sm">
                                {[
                                    ["🇺🇸", "USA — AP · IB · SAT · Common Core", "/online-tuition-usa"],
                                    ["🇬🇧", "UK — GCSE · A-Level · 11+ · IB", "/online-tuition-uk"],
                                    ["🇦🇺", "Australia — VCE · HSC · NAPLAN", "/online-tuition-australia"],
                                    ["🇨🇦", "Canada — Ontario · BC · IB · AP", "/online-tuition-canada"],
                                ].map(([flag, t, h]) => (
                                    <li key={t}>
                                        <Link to={h} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-white/10 group transition-colors">
                                            <span className="text-lg">{flag}</span>
                                            <span className="flex-1 text-blue-50">{t}</span>
                                            <ArrowRight className="w-4 h-4 text-blue-300 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all" />
                                        </Link>
                                    </li>
                                ))}
                                <li className="pt-2 border-t border-white/10 mt-3">
                                    <Link to="/foreign-students" className="text-amber-400 text-xs uppercase tracking-[0.15em] font-bold inline-flex items-center gap-1 hover:gap-2 transition-all">
                                        + 23 more countries via NRI program <ArrowRight className="w-3 h-3" />
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* FUTURE VISION */}
            <section className="py-20 bg-white" data-testid="about-future">
                <div className="max-w-5xl mx-auto px-6 lg:px-8 text-center">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-[#3B82F6] mb-3">The road ahead</div>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] leading-tight mb-6">
                        Where we're going next.
                    </h2>
                    <p className="text-slate-700 text-base sm:text-lg leading-relaxed max-w-3xl mx-auto mb-12">
                        We are building Anand Tutorials into India's most respected global tutoring brand — recognised for the quality of an Indian teacher, not just the price. Three commitments shape our next decade.
                    </p>
                    <div className="grid md:grid-cols-3 gap-5 text-left">
                        {[
                            { I: Users, t: "100,000 students by 2028", d: "Doubling our reach across India and the diaspora — without diluting tutor quality. Stricter selection, deeper training, smarter matching." },
                            { I: Globe2, t: "50 countries served by 2030", d: "Country-specific programs for the US, UK, Australia, Canada, UAE and beyond — taught by Indian-origin mentors who understand both worlds." },
                            { I: BookOpen, t: "Every grade, every board, every dream", d: "From spoken English to Olympiads, NEET to Oxbridge admissions — building the most complete 1:1 academic support system on the internet." },
                        ].map((c) => (
                            <div key={c.t} className="bg-[#F8FAFC] border border-slate-200 rounded-2xl p-6">
                                <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mb-4">
                                    <c.I className="w-6 h-6" />
                                </div>
                                <div className="font-heading font-bold text-[#0F172A] mb-2">{c.t}</div>
                                <p className="text-sm text-slate-600 leading-relaxed">{c.d}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TRUST STRIP */}
            <section className="py-12 bg-[#F8FAFC] border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 grid sm:grid-cols-2 md:grid-cols-4 gap-6 text-center">
                    {[
                        [Users, "50,000+ Students", "across 12 years"],
                        [Award, "98% Pass Rate", "Boards · NEET · JEE"],
                        [BookOpen, "All Major Boards", "CBSE · ICSE · IGCSE · IB · AP"],
                        [Globe2, "27 Countries", "and growing"],
                    ].map(([I, t, d]) => (
                        <div key={t}>
                            <div className="w-14 h-14 rounded-2xl bg-white border border-slate-200 text-[#1E3A8A] flex items-center justify-center mx-auto mb-3 shadow-sm">
                                <I className="w-6 h-6" />
                            </div>
                            <div className="font-heading text-lg font-bold text-[#0F172A]">{t}</div>
                            <div className="text-xs text-slate-500 mt-0.5 uppercase tracking-[0.15em]">{d}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-20 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF] text-white relative overflow-hidden" data-testid="about-final-cta">
                <div className="absolute inset-0 bg-grid opacity-10"></div>
                <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
                    <div className="text-xs font-semibold uppercase tracking-[0.3em] text-amber-400 mb-4">Start the conversation</div>
                    <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-5">
                        Your child only gets one academic year at a time.
                    </h2>
                    <p className="text-blue-100 text-lg sm:text-xl max-w-2xl mx-auto mb-8 leading-relaxed">
                        Let's not waste this one. Book a free 1:1 demo with a verified Anand Tutor — no payment, no commitment, just one honest hour.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <Link
                            to="/register"
                            data-testid="about-final-demo"
                            className="inline-flex items-center gap-2 bg-amber-500 text-[#0F172A] font-bold px-7 py-3.5 rounded-lg hover:bg-amber-400 transition-colors"
                        >
                            <Sparkles className="w-4 h-4" /> Book a Free Demo
                        </Link>
                        <a
                            href={`https://wa.me/${SOCIAL.whatsapp}?text=${encodeURIComponent("Hi Anand Tutorials, I read your about page and would like to know more about 1:1 tuition.")}`}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="about-final-whatsapp"
                            className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-[#1ebe5a]"
                        >
                            <MessageCircle className="w-4 h-4" /> WhatsApp Us
                        </a>
                        <a
                            href="tel:+917000530316"
                            data-testid="about-final-call"
                            className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/15"
                        >
                            <Phone className="w-4 h-4" /> Call Us
                        </a>
                    </div>
                    <div className="mt-10 grid grid-cols-3 gap-4 max-w-md mx-auto pt-8 border-t border-white/10">
                        {[
                            ["Free", "Demo class"],
                            ["No", "Lock-in"],
                            ["12 hr", "Response"],
                        ].map(([n, l]) => (
                            <div key={l}>
                                <div className="font-heading text-xl font-bold text-amber-400">{n}</div>
                                <div className="text-xs uppercase tracking-[0.2em] text-blue-200 mt-1">{l}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* V65 — AEO Conversational FAQ */}
            <ConversationalFAQ
                heading="Deeper questions about Anand Tutorials"
                subheading="Long-form answers to the questions parents most often ask before starting a 1:1 engagement with us."
                items={ABOUT_AEO.faqs}
                testId="about-conversational-faq"
            />
        </div>
    );
}
